import {
  ANALYTICS_PATH,
  CREATE_MENU_PATHS,
  EVENT_MENU_PATHS,
  FLEX_PACKAGE_PAGE_PATHS,
  MARKETING_MENU_PATHS,
  PACKAGE_MENU_LINKS,
  PACKAGE_PAGE_PATHS,
  PROFILE_MENU_PATHS,
} from '../Navigation/navMenuPaths'
import { everyReportingPath } from '../Navigation/reportingMenuSections'

const joinRoutePath = (parent, route) => {
  if (route.index) return parent || '/'
  const path = String(route.path || '')
  if (!path) return parent || '/'
  if (path.startsWith('/')) return path.replace(/\/+$/, '') || '/'
  const base = String(parent || '').replace(/\/+$/, '')
  return `${base}/${path}`.replace(/\/{2,}/g, '/')
}

const collectPaths = (routes, parent = '') => {
  const paths = []
  ;(routes || []).forEach((route) => {
    const full = joinRoutePath(parent, route)
    if (route.path || route.index) paths.push(full)
    if (route.children) paths.push(...collectPaths(route.children, full))
  })
  return paths
}

const whereRouteTree = [
  {
    where: 'Events',
    routes: [
      {
        path: '/event/:uuid',
        children: [
          { index: true },
          { path: 'event-details' },
          { path: 'event-settings' },
          { path: 'waitlist' },
          { path: 'invoice/:orderId/:stripeInvoiceId?' },
          { path: 'seatmap' },
          { path: 'seatmap/assign' },
          { path: 'seatmap/assign/:id' },
          { path: 'offers' },
          { path: 'offers/create' },
          { path: 'offers/edit' },
          { path: 'dynamic-pricing' },
          { path: 'dynamic-pricing/edit' },
          { path: 'orders' },
          { path: 'guest-list' },
          { path: 'guest-list/add' },
          { path: 'attendees' },
          { path: 'primary-tickets' },
          { path: 'resale-tickets' },
          { path: 'daily-ticket-counts' },
          { path: 'daily-ticket-counts/add' },
          { path: 'daily-ticket-counts/edit' },
          { path: 'contact-attendees' },
          { path: 'contact-attendees/add' },
          { path: 'contact-attendees/edit' },
          { path: 'tracking-links' },
          { path: 'tracking-links/add' },
          { path: 'purchaser-export' },
          { path: 'assign-tickets' },
        ],
      },
    ],
  },
  {
    where: 'Packages',
    routes: [
      { path: '/assign-inventory' },
      { path: '/flex-packages' },
      { path: '/season-packages' },
      { path: '/group-purchases' },
      {
        path: '/package/:uuid',
        children: [
          { index: true },
          { path: 'package-details' },
          { path: 'package-settings' },
          { path: 'seatmap' },
          { path: 'seatmap/assign' },
          { path: 'assign-packages' },
          { path: 'assign-packages/fan/:id/profile' },
          { path: 'package-holders' },
          { path: 'package-holders/fan/:id/profile' },
        ],
      },
      {
        path: '/flex-package/:uuid',
        children: [
          { index: true },
          { path: 'flex-package-details' },
          { path: 'assign-flex-packages' },
          { path: 'flex-package-holders' },
          { path: 'package-settings' },
        ],
      },
    ],
  },
  { where: 'Orders', routes: [{ path: '/orders' }] },
  { where: 'Users', routes: [{ path: '/users' }] },
  { where: 'Deposits', routes: [{ path: '/deposits' }] },
  {
    where: 'Reporting',
    routes: everyReportingPath().map((path) => ({ path })),
  },
  {
    where: 'Marketing',
    routes: [
      { path: '/fundraisers' },
      { path: '/promo-codes' },
      { path: '/promo-emails' },
    ],
  },
  {
    where: 'Create',
    routes: [{ path: '/events/create' }, { path: '/access-passes' }],
  },
  {
    where: 'Profile',
    routes: [
      { path: '/analytics' },
      {
        path: '/settings',
        children: [
          { path: 'organization-information' },
          { path: 'organization-settings' },
          { path: 'email-templates' },
          { path: 'marketing-attribution' },
          { path: 'team-management' },
          { path: 'security' },
          { path: 'account-information' },
          { path: 'account-information/payouts' },
          { path: 'account-information/payouts/:id' },
          { path: 'payouts' },
          { path: 'tax-status' },
          { path: 'terminals' },
          { path: 'scanners' },
          { path: 'custom-fees' },
          { path: 'buyer-types' },
          {
            path: 'marketplace',
            children: [
              { path: 'items' },
              { path: 'items/new' },
              { path: 'items/:id/edit' },
              { path: 'categories' },
              { path: 'modifiers' },
              { path: 'locations' },
              { path: 'orders' },
              { path: 'import-export' },
            ],
          },
          { path: 'donations' },
        ],
      },
    ],
  },
]

const walkedPaths = (where) => {
  const group = whereRouteTree.find((entry) => entry.where === where)
  return collectPaths(group?.routes || [])
}

const inWalk = (paths, where) => {
  const known = new Set(walkedPaths(where))
  return paths.filter((path) => known.has(path))
}

const suggestionsFor = (
  where,
  { reportingPaths, analyticsVisible = false } = {}
) => {
  if (where === 'Events') return inWalk(EVENT_MENU_PATHS, where)
  if (where === 'Packages') {
    return inWalk(
      [
        ...PACKAGE_MENU_LINKS.map((link) => link.to),
        ...PACKAGE_PAGE_PATHS,
        ...FLEX_PACKAGE_PAGE_PATHS,
      ],
      where
    )
  }
  if (where === 'Orders') return inWalk(['/orders'], where)
  if (where === 'Users') return inWalk(['/users'], where)
  if (where === 'Deposits') return inWalk(['/deposits'], where)
  if (where === 'Reporting') {
    const source = Array.isArray(reportingPaths)
      ? reportingPaths
      : everyReportingPath()
    return inWalk(source, where)
  }
  if (where === 'Marketing') return inWalk(MARKETING_MENU_PATHS, where)
  if (where === 'Create') return inWalk(CREATE_MENU_PATHS, where)
  if (where === 'Profile') {
    const paths = analyticsVisible
      ? [...PROFILE_MENU_PATHS, ANALYTICS_PATH]
      : PROFILE_MENU_PATHS
    return inWalk(paths, where)
  }
  return []
}

const isUrlSearch = (suggestions) => (suggestions || []).length > 1

const filterUrlSuggestions = (suggestions, query, expandExact = true) => {
  const list = suggestions || []
  if (!isUrlSearch(list)) return list
  const text = String(query || '')
    .trim()
    .toLowerCase()
  if (!text) return list
  if (expandExact && list.some((path) => path.toLowerCase() === text)) {
    return list
  }
  const needle = text.startsWith('/') ? text : `/${text}`
  return list.filter((path) => path.toLowerCase().includes(needle))
}

const nextActionPath = (currentPath, suggestions) => {
  const list = suggestions || []
  if (list.length === 1) return list[0]
  if (currentPath && list.includes(currentPath)) return currentPath
  return ''
}

const WHERE_OPTIONS = whereRouteTree.map((entry) => entry.where)

export {
  WHERE_OPTIONS,
  filterUrlSuggestions,
  isUrlSearch,
  nextActionPath,
  suggestionsFor,
  walkedPaths,
  whereRouteTree,
}
